import {
  type CSSProperties,
  type KeyboardEventHandler,
  type MouseEvent,
  type MouseEventHandler,
  type MutableRefObject,
  type ReactNode,
  useRef,
} from 'react';
import styled from 'styled-components';
import { consumeEvent } from '@frameio/utils';
import { theme, useSharedHoverContext } from '@frameio/vapor';
import type { ProjectTreeItemType } from '../../../types';
import { Border } from './Border';
import { Chevron } from './Chevron';
import { Content } from './Content';
import { Loading } from './Loading';

type Props = {
  adornmentAfter?: ReactNode;
  children: any;
  contentRef?: MutableRefObject<HTMLDivElement | null>;
  expandable?: boolean;
  id: string;
  isActive?: boolean;
  isDisabled?: boolean;
  isLink: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  name?: string;
  onChevronClick?: (event: MouseEvent) => void;
  onClick?: MouseEventHandler;
  onKeyDown?: KeyboardEventHandler;
  onMouseDown?: MouseEventHandler;
  paddingLeft?: number;
  style?: CSSProperties;
  type: ProjectTreeItemType;
};

export const ItemContainer = ({
  adornmentAfter,
  children,
  contentRef,
  expandable = false,
  id,
  isActive = false,
  isDisabled = false,
  isLink,
  isLoading = false,
  isOpen = false,
  name,
  onChevronClick,
  onClick,
  onKeyDown,
  onMouseDown,
  paddingLeft,
  style,
  type,
}: Props) => {
  const { onEnterTarget, onLeaveTarget } = useSharedHoverContext();
  const iconRef = useRef(null);

  const onMouseEnter = (e: MouseEvent) => {
    onEnterTarget(rectToObject(e.currentTarget.getBoundingClientRect()));
  };

  const onMouseLeave = () => {
    onLeaveTarget();
  };

  const bind = {
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
  };

  return (
    <Container
      aria-current={isActive ? 'page' : undefined}
      aria-expanded={isOpen}
      aria-label={name}
      data-asset-id={id}
      data-dd-action-name={`project-tree.${type}`}
      data-testid={`project-tree--${type}`}
      data-tree-item-type={type}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={isLink && !isDisabled ? 'link' : 'button'}
      style={style}
      tabIndex={isDisabled ? undefined : 0}
      {...bind}
    >
      <InnerContainer $isActive={isActive} $paddingLeft={paddingLeft}>
        {expandable && (
          <Chevron
            ref={iconRef}
            $isOpen={isOpen}
            color={theme.colors.icon.primary}
            onClick={consumeEvent}
            onMouseDown={onChevronClick}
            role="button"
            size={CHEVRON_SIZE}
          />
        )}
        <Border />

        <Content ref={contentRef}>{children}</Content>

        {adornmentAfter && !isLoading && (
          <AdornmentContainer>{adornmentAfter}</AdornmentContainer>
        )}
        <Loading isLoading={isLoading} />
      </InnerContainer>
    </Container>
  );
};

const CHEVRON_SIZE = 16;

export const itemColorStates = {
  hover: theme.colors.background.interactive.hover,
  activated: theme.colors.background.interactive.activatedSolid,
  default: theme.colors.background.panel,
  over: theme.colors.brand.primaryAlphaLow,
  overBorder: theme.colors.brand.primary,
} as const;

const { eases, durations } = theme.transitions;

export const ITEM_HEIGHT = 32;
export const ICON_WIDTH = 24;

// @todo (ASSETS-218): Update styled div to be button elements
export const InnerContainer = styled.div<{
  $isActive: boolean;
  $paddingLeft?: number;
}>`
  align-items: center;
  background: ${(p) =>
    p.$isActive ? itemColorStates.activated : 'transparent'};
  border-radius: ${theme.spaces[6]};
  cursor: pointer;
  display: flex;
  height: 100%;
  outline: none;
  padding: 0 ${theme.spaces[4]} 0 ${theme.spaces[6]};
  transform: perspective(0px);
  transition: all ${durations[100]} ${eases.primary};
  width: 100%;

  &:hover {
    background: ${(p) => (!p.$isActive ? itemColorStates.hover : undefined)};
  }

  ${Border} {
    border: solid 1px transparent;
  }

  ${(p) =>
    p.$paddingLeft &&
    `
    padding-left: calc(${theme.spaces[6]} + ${p.$paddingLeft}px);
  `}
`;

const AdornmentContainer = styled.div`
  flex-shrink: 0;

  & > * {
    visibility: hidden;
  }
`;

// @todo (ASSETS-218): Update styled div to be button elements
const Container = styled.div`
  cursor: pointer;
  height: ${theme.spaces[ITEM_HEIGHT]};
  outline: none;
  position: relative;
  width: 100%;

  &:focus-visible ${InnerContainer} {
    outline: 1px solid ${theme.colors.border.interactive.focused};
    outline-offset: -1px;
  }

  &[aria-current='page'] ${AdornmentContainer} > *,
  &:hover ${AdornmentContainer} > *,
  &:focus-within ${AdornmentContainer} > * {
    visibility: visible;
  }
`;

const rectToObject = (bounds: any) => ({
  top: bounds.top,
  left: bounds.left,
  right: bounds.right,
  bottom: bounds.bottom,
  width: bounds.width,
  height: bounds.height,
});

const PADDING = 16;
const ROOT_FOLDER_ADJUSTMENT = 16;
const MAX_DEPTH = 18;

export const calculatePadding = (baseDepth: number, depth: number) =>
  Math.min(MAX_DEPTH, depth - baseDepth) * PADDING - ROOT_FOLDER_ADJUSTMENT;
